import { toast } from "@rubi/ui";

type Options = {
  [key: string]: any;
  method?: string;
};

const extractStatusBody = (response) =>
  response.json().then((rJson) => ({
    status: response.status,
    body: rJson,
  }));

// A 401 from these pages is the endpoint's own answer -- bad credentials, or a
// spent/expired reset token -- not an expired session, so don't bounce to login.
const SELF_AUTHENTICATING_PATHS = [
  "/login",
  "/verifyphone",
  "/confirmotp",
  "/verifyemail",
  "/view-alert",
  "/resetpassword",
  "/setpassword",
];

const statusTriaging = (
  { status, body },
  errorCallback: ((status: number, body: any) => void) | null = null,
) => {
  if (status >= 400 && status <= 599) {
    if (errorCallback) {
      errorCallback(status, body);
    } else {
      if (status === 401) {
        console.log("In STATUS TRIAGING 401");
        if (body?.detail?.msg) {
          toast.error(JSON.stringify(body.detail.msg));
        }
        if (
          !SELF_AUTHENTICATING_PATHS.some((path) =>
            window.location.pathname.includes(path),
          )
        ) {
          toast.error("Your session has expired. \n\nRedirecting to login...");
          setTimeout(() => {
            window.location.href = "/login";
          }, 3000);
        }
        return Promise.reject("Unauthorized");
      }

      if (status === 404) {
        return Promise.reject("Not Found");
      }

      if (body?.detail?.msg) {
        toast.error(JSON.stringify(body.detail.msg));
      } else if (body?.detail) {
        toast.error(JSON.stringify(body.detail));
      } else {
        toast.error(
          "Error " +
            status +
            ". Please try again. If you need support contact support@tryrubicon.com.",
        );
      }
      console.log("In STATUS TRIAGING 400-599");
    }

    return Promise.reject(body);
    // throw Error(body);
  }

  return body;
};

// Set header, conver body to string before fetching
const setOptions = (options: Options) => {
  if (!options.headers) {
    options.headers = new Headers();
  }

  if (
    typeof options?.body != "string" &&
    !(options?.body instanceof FormData)
  ) {
    options.body = JSON.stringify(options.body);
    options.headers.append("Content-Type", "application/json");
  }

  return options;
};

export const post = (
  url,
  options: Options = {},
  errorCallback: ((status: number, body: any) => void) | null = null,
) => {
  options.method = "POST";
  options = setOptions(options);

  return fetch(url, options)
    .then(extractStatusBody)
    .then(({ status, body }) =>
      statusTriaging({ status, body }, errorCallback),
    );
};

export const patch = (
  url,
  options: Options = {},
  errorCallback: ((status: number, body: any) => void) | null = null,
) => {
  options.method = "PATCH";
  options = setOptions(options);
  return fetch(url, options)
    .then(extractStatusBody)
    .then(({ status, body }) =>
      statusTriaging({ status, body }, errorCallback),
    );
};

export const get = (
  url,
  options: Options = {},
  errorCallback: ((status: number, body: any) => void) | null = null,
) => {
  options.method = "GET";
  return fetch(url, options)
    .then(extractStatusBody)
    .then(({ status, body }) =>
      statusTriaging({ status, body }, errorCallback),
    );
};

export const put = (
  url,
  options: Options = {},
  errorCallback: ((status: number, body: any) => void) | null = null,
) => {
  options.method = "PUT";
  options = setOptions(options);
  return fetch(url, options)
    .then(extractStatusBody)
    .then(({ status, body }) =>
      statusTriaging({ status, body }, errorCallback),
    );
};

export const del = (
  url,
  options: Options = {},
  errorCallback: ((status: number, body: any) => void) | null = null,
) => {
  options.method = "DELETE";
  options = setOptions(options);
  return fetch(url, options)
    .then(extractStatusBody)
    .then(({ status, body }) =>
      statusTriaging({ status, body }, errorCallback),
    );
};

export const fetchJson = async (url: string, options: RequestInit = {}) => {
  const headers = new Headers({
    Accept: "application/json",
    "Content-Type": "application/json",
  });

  // if the request host is *tryrubicon.com then force enable sending credentials
  if (new URL(url).hostname.match(/tryrubicon.com$/)) {
    options.credentials = "include";
  }

  return fetch(url, { ...options, headers: headers })
    .then((response) =>
      response.text().then((text) => ({
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        body: text,
      })),
    )
    .then(({ status, statusText, headers, body }) => {
      let json;
      try {
        json = JSON.parse(body);
      } catch (e) {
        // not json, no big deal
      }
      if (status === 401) {
        return Promise.reject(
          new Error("Unauthorized or Credentials expired, Please re-login"),
        );
      }
      if (status === 409) {
        return Promise.reject(
          new Error((json && json.error && json.error.message) || statusText),
        );
      }

      if (status === 422) {
        let message = "**VALIDATION FAILED** ";
        if (json.error && json.error.details && json.error.details.length > 0) {
          json.error.details.forEach((element: any) => {
            const f = element;
            const mf = f.path + " " + f.message;
            message = message + " , " + mf;
          });
        }
        return Promise.reject(new Error(message));
      }

      if (status < 200 || status >= 300) {
        return Promise.reject(new Error((json && json.message) || statusText));
      }
      return Promise.resolve({ status, headers, body, json });
    });
};
